import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { Briefcase, Loader2, ArrowRight } from "lucide-react";

export default function RealWorldWriting() {
  const [contexts, setContexts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [topic, setTopic] = useState("");
  const [draft, setDraft] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/real-world/contexts").then(r => {
      setContexts(r.data.contexts || []);
      setSelected(r.data.contexts?.[0]?.id);
    });
  }, []);

  const submit = async () => {
    if (!selected) return;
    if (!topic.trim()) { toast.error("Add a topic first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/real-world/coach", { context_id: selected, topic, student_draft: draft });
      setResult(data);
      celebrateBadges(data._meta);
    } catch (e) {
      if (e.response?.status === 402) toast.error(e.response.data.detail.message);
      else toast.error("Could not fetch coaching.");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center"><Briefcase size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Real-World Writing</h1>
        </div>
        <p className="text-slate-600 max-w-2xl">Write for the audiences you'll actually meet: scholarship committees, employers, journalists, speech audiences, judges. Coaching adapts to the genre.</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-5">
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2 block">Pick a writing context</label>
          <div className="flex flex-wrap gap-2">
            {contexts.map(c => (
              <button key={c.id} data-testid={`rw-context-${c.id}`} type="button"
                onClick={()=>setSelected(c.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold border-2 ${selected===c.id ? 'bg-amber-400 border-amber-500 text-slate-900' : 'bg-white border-slate-200 text-slate-700 hover:border-amber-300'}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Topic / situation</label>
          <input data-testid="rw-topic" value={topic} onChange={(e)=>setTopic(e.target.value)}
            placeholder='e.g. "Email to my history teacher asking for a recommendation letter"'
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 px-4 py-3 outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your draft (optional)</label>
          <textarea data-testid="rw-draft" value={draft} onChange={(e)=>setDraft(e.target.value)} rows={6}
            placeholder="Drop in a first attempt for genre-specific feedback."
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 px-4 py-3 outline-none resize-none" />
        </div>
        <button data-testid="rw-submit" onClick={submit} disabled={loading}
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-6 py-3 tactile-shadow-amber inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Coaching…</> : "Coach me"}
        </button>
      </div>

      {result && (
        <div data-testid="rw-result" className="space-y-4 fade-up">
          <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
            <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-1">Why this genre works</div>
            <p className="text-slate-900">{result.genre_brief}</p>
          </div>
          <div className="tactile-card p-6 bg-amber-50 border-amber-100">
            <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-1">Audience lens</div>
            <p className="text-slate-900">{result.audience_lens}</p>
          </div>
          <div className="tactile-card p-6">
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-3">Structure</div>
            <ol className="space-y-3">
              {(result.structure_outline || []).map((s, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-indigo-600 text-white font-bold grid place-items-center text-sm flex-shrink-0">{i+1}</div>
                  <div>
                    <div className="font-bold text-slate-900">{s.step}</div>
                    <div className="text-sm text-slate-700"><strong className="text-emerald-700">Do:</strong> {s.do_this}</div>
                    {s.avoid && <div className="text-sm text-slate-700"><strong className="text-rose-700">Avoid:</strong> {s.avoid}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="tactile-card p-6 bg-emerald-50 border-emerald-100">
              <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-2">Opening strategies</div>
              <ul className="list-disc pl-5 space-y-1 text-slate-800">{(result.opening_strategies||[]).map((s,i)=><li key={i}>{s}</li>)}</ul>
            </div>
            <div className="tactile-card p-6 bg-rose-50 border-rose-100">
              <div className="text-xs uppercase font-bold tracking-widest text-rose-800 mb-2">Signature moves</div>
              <ul className="list-disc pl-5 space-y-1 text-slate-800">{(result.signature_moves||[]).map((s,i)=><li key={i}>{s}</li>)}</ul>
            </div>
          </div>
          {result.draft_feedback && (
            <div className="tactile-card p-6 bg-violet-50 border-violet-100">
              <div className="text-xs uppercase font-bold tracking-widest text-violet-800 mb-1">Coach feedback</div>
              <p className="text-slate-900">{result.draft_feedback}</p>
            </div>
          )}
          {result.next_step && (
            <div className="tactile-card p-6 bg-indigo-50 border-indigo-100">
              <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-1">Next step</div>
              <p className="text-slate-900 font-semibold inline-flex items-center gap-2">{result.next_step} <ArrowRight size={14}/></p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
